import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/lib/hooks/useToast'
import {
  twoFactorService,
  type TwoFactorStatusResponse,
} from '@/lib/services/two-factor'
import { ShieldCheck, ShieldAlert, Key } from 'lucide-react'
import { useHasPermission } from '@/lib/hooks/usePermission'

export function TwoFactorSettings() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const canManage2FA = useHasPermission('auth.2fa.manage')
  const [status, setStatus] = useState<TwoFactorStatusResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [regeneratePassword, setRegeneratePassword] = useState('')
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false)
  const [showBackupCodesDialog, setShowBackupCodesDialog] = useState(false)
  const [backupCodes, setBackupCodes] = useState<string[]>([])

  const tRef = useRef(t)
  const toastRef = useRef(toast)

  useEffect(() => {
    tRef.current = t
    toastRef.current = toast
  }, [t, toast])

  const loadStatus = useCallback(async () => {
    try {
      setIsLoading(true)
      const statusData = await twoFactorService.getStatus()
      setStatus(statusData)
    } catch (error) {
      console.error('Failed to load 2FA status:', error)
      toastRef.current({
        title: tRef.current('algemeen.fout', 'Error'),
        description: tRef.current('auth.twoFactorStatusError', 'Failed to load 2FA status'),
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  if (!canManage2FA) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">
          {t('profile.no2FAPermission', 'U heeft geen toestemming om 2FA in te stellen.')}
        </p>
      </div>
    )
  }

  const handleRegenerateStart = () => {
    setShowRegenerateDialog(true)
  }

  const handleRegenerateBackupCodes = async () => {
    if (!regeneratePassword) {
      toast({
        title: t('algemeen.fout', 'Error'),
        description: t('auth.passwordRequired', 'Please enter your password'),
        variant: 'destructive',
      })
      return
    }

    try {
      setIsLoading(true)
      const result = await twoFactorService.regenerateBackupCodes(regeneratePassword)
      setBackupCodes(result.backupCodes)
      setShowRegenerateDialog(false)
      setShowBackupCodesDialog(true)
      setRegeneratePassword('')
      toast({
        title: t('algemeen.succes', 'Success'),
        description: t('auth.backupCodesRegenerated', 'Backup codes have been regenerated'),
        variant: 'success',
      })
    } catch (error) {
      console.error('Failed to regenerate backup codes:', error)
      toast({
        title: t('algemeen.fout', 'Error'),
        description: t(
          'auth.regenerateFailed',
          'Failed to regenerate backup codes. Check your password.'
        ),
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const downloadBackupCodes = () => {
    const content = backupCodes.join('\n')
    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'crisis-buddy-backup-codes.txt'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'))
    toast({
      title: t('algemeen.succes', 'Success'),
      description: t('auth.backupCodesCopied', 'Backup codes copied to clipboard'),
      variant: 'success',
    })
  }

  if (isLoading && !status) {
    return (
      <div className="flex justify-center py-8">
        <p>{t('algemeen.laden', 'Loading...')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            {status?.enabled ? (
              <ShieldCheck className="h-6 w-6 text-green-600" />
            ) : (
              <ShieldAlert className="h-6 w-6 text-yellow-600" />
            )}
            <div>
              <CardTitle>
                {t('auth.twoFactorAuthentication', 'Two-Factor Authentication')}
              </CardTitle>
              <CardDescription>
                {status?.enabled
                  ? t(
                      'auth.twoFactorEnabledDescription',
                      '2FA is currently enabled for your account'
                    )
                  : t(
                      'auth.twoFactorDisabledDescription',
                      'Add an extra layer of security to your account'
                    )}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {status?.enabled && (
            <>
              {status.lastUsed && (
                <div className="bg-muted p-3 rounded-md text-sm text-muted-foreground">
                  {t('auth.lastUsed', 'Last used')}: {new Date(status.lastUsed).toLocaleString()}
                </div>
              )}
              <div className="flex gap-3">
                <Button variant="outline" onClick={handleRegenerateStart} disabled={isLoading}>
                  <Key className="h-4 w-4 mr-2" />
                  {t('auth.regenerateBackupCodes', 'Regenerate Backup Codes')}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Regenerate Backup Codes Dialog */}
      <Dialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('auth.regenerateBackupCodes', 'Regenerate Backup Codes')}</DialogTitle>
            <DialogDescription>
              {t(
                'auth.regenerateDescription',
                'Enter your password to generate new backup codes. Your old codes will no longer work.'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="regenerate-password">{t('auth.password', 'Password')}</Label>
              <Input
                id="regenerate-password"
                type="password"
                value={regeneratePassword}
                onChange={e => setRegeneratePassword(e.target.value)}
                placeholder={t('auth.passwordPlaceholder', 'Enter your password')}
                disabled={isLoading}
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowRegenerateDialog(false)
                  setRegeneratePassword('')
                }}
                disabled={isLoading}
              >
                {t('auth.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleRegenerateBackupCodes}
                disabled={isLoading || !regeneratePassword}
              >
                {isLoading ? t('algemeen.laden', 'Loading...') : t('auth.regenerate', 'Regenerate')}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog (for regeneration) */}
      <Dialog open={showBackupCodesDialog} onOpenChange={setShowBackupCodesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('auth.newBackupCodes', 'New Backup Codes')}</DialogTitle>
            <DialogDescription>
              {t(
                'auth.backupCodesDescription',
                'Save these codes in a safe place. Your old codes are no longer valid.'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted p-4 rounded-lg font-mono text-sm space-y-2">
              {backupCodes.map((code, index) => (
                <div key={index} className="text-center">
                  {code}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={copyBackupCodes}>
                {t('auth.copy', 'Copy')}
              </Button>
              <Button variant="outline" className="flex-1" onClick={downloadBackupCodes}>
                {t('auth.download', 'Download')}
              </Button>
            </div>
            <Button
              className="w-full"
              onClick={() => {
                setShowBackupCodesDialog(false)
                setBackupCodes([])
              }}
            >
              {t('auth.done', 'Done')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
